'use client';

import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';

import { createTracker, type PulseTracker } from '../tracker';
import type { PulseTrackerConfig } from '../types';

const PulseContext = createContext<PulseTracker | null>(null);

type PulseProviderProps = PropsWithChildren<{
    config: PulseTrackerConfig;
}>;

export function PulseProvider({ config, children }: PulseProviderProps) {
    const tracker = useMemo(() => createTracker(config), [config]);

    return (
        <PulseContext.Provider value={tracker}>
            {children}
        </PulseContext.Provider>
    );
}

/**
 * Access the pulse tracker from any component within PulseProvider.
 */
export function usePulseTracker(): PulseTracker {
    const tracker = useContext(PulseContext);
    if (!tracker) {
        throw new Error(
            'usePulseTracker must be used within a PulseProvider'
        );
    }
    return tracker;
}

/**
 * Same as usePulseTracker, but returns null instead of throwing when there is no PulseProvider.
 *
 * For shared components that want to report a pulse event where a provider exists (the consumer
 * apps) without breaking the apps that never mount one — the operator tools render the same
 * components but have no pulse pipeline. Tracking is optional; rendering is not.
 */
export function usePulseTrackerOptional(): PulseTracker | null {
    return useContext(PulseContext);
}
